import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { Lock, LockOpen } from 'lucide-react';
import { useState } from 'react';
import { Field, Textarea } from '@/components/form';
import { EmptyState, PageHeader } from '@/components/page-header';
import { toast } from '@/components/toast';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ApiError, api } from '@/lib/api';
import { ltr } from '@/lib/arabic';
import { formatDate } from '@/lib/format';
import { i18n, useT } from '@/lib/i18n';
import { useOrgId } from '@/lib/org';
import {
  monthLabel,
  monthShort,
  periodsToLock,
  periodsToUnlock,
  rangeLabel,
  usePeriods,
  type FiscalYear,
  type Period,
  type PeriodsOverview,
} from '@/lib/periods';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/accounting/periods/')({ component: PeriodsPage });

const STATUS: Record<
  FiscalYear['status'],
  { label: () => string; variant: 'outline' | 'secondary' }
> = {
  open: { label: () => i18n.t('accountingPages.periodsPage.statusOpen'), variant: 'outline' },
  locked: { label: () => i18n.t('accountingPages.periodsPage.statusLocked'), variant: 'outline' },
  closed: { label: () => i18n.t('accountingPages.periodsPage.statusClosed'), variant: 'secondary' },
};

/** Screen A5 (document 06): financial years with a row of month locks, lock/unlock with reason. */
function PeriodsPage() {
  const t = useT();
  const orgId = useOrgId();
  const qc = useQueryClient();
  const periods = usePeriods();
  const [action, setAction] = useState<{ kind: 'lock' | 'unlock'; period: Period } | null>(null);
  const addYear = useMutation({
    mutationFn: () => api<PeriodsOverview>(`/orgs/${orgId}/fiscal-years`, { method: 'POST' }),
    onSuccess: async (o) => {
      await qc.invalidateQueries({ queryKey: ['periods', orgId] });
      const last = o.fiscalYears[o.fiscalYears.length - 1];
      toast(t('accountingPages.periodsPage.yearAdded', { label: last?.label ?? '' }));
    },
    onError: (e) => toast(e.message),
  });

  if (!orgId)
    return (
      <EmptyState
        title={t('shell.selectClient')}
        hint={t('accountingPages.periodsPage.selectClientHint')}
      />
    );
  if (periods.isPending)
    return <p className="text-sm text-muted-foreground">{t('common.loading')}</p>;
  if (periods.isError)
    return (
      <EmptyState title={t('accountingPages.periodsPage.loadError')} hint={periods.error.message} />
    );
  const o = periods.data;
  const years = [...o.fiscalYears].reverse();

  return (
    <div>
      <PageHeader
        title={t('accountingPages.periodsPage.title')}
        description={t('accountingPages.periodsPage.description')}
        actions={
          o.canManage ? (
            <Button variant="outline" disabled={addYear.isPending} onClick={() => addYear.mutate()}>
              {t('accountingPages.periodsPage.addYear')}
            </Button>
          ) : null
        }
      />
      <div
        className="mb-6 flex items-center gap-3 rounded-lg border bg-card px-4 py-3"
        data-testid="lock-date"
      >
        {o.lockDate ? (
          <Lock className="size-5 text-primary" aria-hidden />
        ) : (
          <LockOpen className="size-5 text-muted-foreground" aria-hidden />
        )}
        <div className="text-sm">
          {o.lockDate ? (
            <>
              <div className="font-medium">
                {t('accountingPages.periodsPage.booksClosedThrough', {
                  date: formatDate(o.lockDate),
                })}
              </div>
              <div className="text-muted-foreground">
                {o.lockedAt
                  ? t('accountingPages.periodsPage.lockedByOn', {
                      name: o.lockedByName ?? t('accountingPages.periodsPage.anAccountant'),
                      date: formatDate(o.lockedAt),
                    })
                  : t('accountingPages.periodsPage.lockedBy', {
                      name: o.lockedByName ?? t('accountingPages.periodsPage.anAccountant'),
                    })}
              </div>
            </>
          ) : (
            <div className="font-medium">{t('accountingPages.periodsPage.noneLocked')}</div>
          )}
        </div>
        {!o.canManage ? (
          <p className="ms-auto text-xs text-muted-foreground">
            {t('accountingPages.periodsPage.needsRights')}
          </p>
        ) : null}
      </div>

      <div className="flex flex-col gap-5">
        {years.map((fy) => (
          <FiscalYearCard
            key={fy.id}
            fy={fy}
            canManage={o.canManage}
            onAction={(kind, period) => setAction({ kind, period })}
          />
        ))}
      </div>
      <LockDialog overview={o} action={action} onClose={() => setAction(null)} />
    </div>
  );
}

function FiscalYearCard({
  fy,
  canManage,
  onAction,
}: {
  fy: FiscalYear;
  canManage: boolean;
  onAction: (kind: 'lock' | 'unlock', p: Period) => void;
}) {
  const t = useT();
  const status = STATUS[fy.status];
  const hasEntries = fy.periods.some((p) => p.entryCount > 0);
  return (
    <section
      className="rounded-lg border bg-card"
      aria-label={t('accountingPages.periodsPage.fyTitle', { label: fy.label })}
      data-testid={`fy-${fy.label}`}
    >
      <header className="flex flex-wrap items-center gap-3 border-b px-4 py-3">
        <h2 className="text-base font-semibold">
          {t('accountingPages.periodsPage.fyTitle', { label: fy.label })}
        </h2>
        <span className="text-sm text-muted-foreground">
          {formatDate(fy.startDate)} – {formatDate(fy.endDate)}
        </span>
        <Badge variant={status.variant}>{status.label()}</Badge>
        {fy.status === 'closed' ? (
          <span className="text-xs text-muted-foreground">
            {fy.closedAt
              ? t('accountingPages.periodsPage.closedByOn', {
                  name: fy.closedByName ?? t('accountingPages.periodsPage.anAccountant'),
                  date: formatDate(fy.closedAt),
                })
              : t('accountingPages.periodsPage.closedBy', {
                  name: fy.closedByName ?? t('accountingPages.periodsPage.anAccountant'),
                })}
            {fy.closeEntryId ? (
              <>
                {' · '}
                <Link
                  to="/accounting/journal-entries/$entryId"
                  params={{ entryId: fy.closeEntryId }}
                  className="text-primary hover:underline"
                >
                  {t('accountingPages.periodsPage.closeEntryNo', { no: fy.closeEntryNo })}
                </Link>
              </>
            ) : null}
          </span>
        ) : null}
        <div className="ms-auto">
          {fy.status === 'closed' || hasEntries || fy.status === 'locked' ? (
            <Button asChild variant={fy.status === 'locked' ? 'default' : 'outline'} size="sm">
              <Link to="/accounting/periods/$fiscalYearId/close" params={{ fiscalYearId: fy.id }}>
                {fy.status === 'closed'
                  ? t('accountingPages.periodsPage.viewClose')
                  : t('accountingPages.periodsPage.closeYear')}
              </Link>
            </Button>
          ) : null}
        </div>
      </header>
      <ol className="grid grid-cols-3 gap-2 p-4 sm:grid-cols-4 md:grid-cols-6 xl:grid-cols-12">
        {fy.periods.map((p) => (
          <li key={p.id}>
            <MonthTile
              period={p}
              closedYear={fy.status === 'closed'}
              canManage={canManage}
              onAction={onAction}
            />
          </li>
        ))}
      </ol>
    </section>
  );
}

function MonthTile({
  period: p,
  closedYear,
  canManage,
  onAction,
}: {
  period: Period;
  closedYear: boolean;
  canManage: boolean;
  onAction: (kind: 'lock' | 'unlock', p: Period) => void;
}) {
  const t = useT();
  const label = monthLabel(p.startDate);
  const by = p.lockedByName ?? t('accountingPages.periodsPage.anAccountant');
  const lockedNote = p.lockedAt
    ? t('accountingPages.periodsPage.monthLockedByOn', {
        name: by,
        date: ltr(formatDate(p.lockedAt)),
        reason: p.lockReason ?? '',
      })
    : t('accountingPages.periodsPage.monthLockedBy', { name: by, reason: p.lockReason ?? '' });
  const tile = (
    <button
      type="button"
      className={cn(
        'flex w-full flex-col items-start gap-1 rounded-md border px-2.5 py-2 text-start text-xs transition-colors',
        p.locked ? 'border-primary/30 bg-primary/5' : 'bg-background hover:bg-accent',
        !canManage && 'cursor-default',
      )}
      aria-label={
        p.locked
          ? t('accountingPages.periodsPage.monthLockedAria', { month: label })
          : t('accountingPages.periodsPage.monthOpenAria', { month: label })
      }
      data-testid={`month-${p.startDate.slice(0, 7)}`}
      title={p.locked ? lockedNote : undefined}
    >
      <span className="flex w-full items-center justify-between">
        <span className="text-sm font-medium">{monthShort(p.startDate)}</span>
        {p.locked ? (
          <Lock className="size-4 text-primary" aria-hidden />
        ) : (
          <LockOpen className="size-4 text-muted-foreground" aria-hidden />
        )}
      </span>
      <span className={p.locked ? 'font-medium text-primary' : 'text-muted-foreground'}>
        {p.locked ? t('accountingPages.periodsPage.locked') : t('accountingPages.periodsPage.open')}
      </span>
      <span className="text-muted-foreground">
        {p.entryCount === 0
          ? t('accountingPages.periodsPage.noEntries')
          : t('accountingPages.periodsPage.entries', { count: p.entryCount })}
      </span>
      {p.draftCount > 0 ? (
        <span className="text-destructive">
          {t('accountingPages.periodsPage.drafts', { count: p.draftCount })}
        </span>
      ) : null}
      {p.reviewCount > 0 ? (
        <span className="text-warning">
          {t('accountingPages.periodsPage.toReview', { count: p.reviewCount })}
        </span>
      ) : null}
    </button>
  );
  if (!canManage) return tile;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{tile}</DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="z-50 max-w-72">
        <DropdownMenuLabel>{label}</DropdownMenuLabel>
        {p.locked && p.lockReason ? (
          <p className="px-2 pb-1.5 text-xs text-muted-foreground">{lockedNote}</p>
        ) : null}
        <DropdownMenuSeparator />
        {p.locked ? (
          <DropdownMenuItem
            disabled={closedYear}
            onSelect={() => onAction('unlock', p)}
            title={closedYear ? t('accountingPages.periodsPage.reopenFirst') : undefined}
          >
            {t('accountingPages.periodsPage.unlockFrom', { month: label })}
          </DropdownMenuItem>
        ) : (
          <DropdownMenuItem onSelect={() => onAction('lock', p)}>
            {t('accountingPages.periodsPage.lockThrough', { month: label })}
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** ConfirmDialog with a typed reason (document 05): lock and unlock are audited. */
function LockDialog({
  overview,
  action,
  onClose,
}: {
  overview: PeriodsOverview;
  action: { kind: 'lock' | 'unlock'; period: Period } | null;
  onClose: () => void;
}) {
  const t = useT();
  const orgId = useOrgId();
  const qc = useQueryClient();
  const [reason, setReason] = useState('');
  const [warnings, setWarnings] = useState<string[] | null>(null);
  const affected = action
    ? action.kind === 'lock'
      ? periodsToLock(overview, action.period)
      : periodsToUnlock(overview, action.period)
    : [];
  const m = useMutation({
    mutationFn: (confirmWarnings: boolean) =>
      api<PeriodsOverview>(`/orgs/${orgId}/periods/${action!.kind}`, {
        method: 'POST',
        body: JSON.stringify(
          action!.kind === 'lock'
            ? { through: action!.period.startDate, reason: reason.trim(), confirmWarnings }
            : { from: action!.period.startDate, reason: reason.trim() },
        ),
      }),
    onSuccess: async (o) => {
      qc.setQueryData(['periods', orgId], o);
      await qc.invalidateQueries({ queryKey: ['periods', orgId] });
      toast(
        action!.kind === 'lock'
          ? t('accountingPages.periodsPage.toastLocked', {
              date: o.lockDate ? ltr(formatDate(o.lockDate)) : '',
            })
          : t('accountingPages.periodsPage.toastUnlocked', { range: rangeLabel(affected) }),
      );
      close();
    },
    onError: (e) => {
      if (e instanceof ApiError && e.code === 'PERIOD_WARNINGS') {
        const items = (e.details as { items?: string[] } | undefined)?.items;
        setWarnings(items ?? [e.message]);
      }
    },
  });
  const close = () => {
    setReason('');
    setWarnings(null);
    m.reset();
    onClose();
  };
  if (!action) return null;
  const label = monthLabel(action.period.startDate);
  const lock = action.kind === 'lock';
  const tooShort = reason.trim().length < 3;
  const error = m.error && !(m.error instanceof ApiError && m.error.code === 'PERIOD_WARNINGS');
  return (
    <Dialog open onOpenChange={(open) => !open && close()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {lock
              ? t('accountingPages.periodsPage.lockTitle', { month: label })
              : t('accountingPages.periodsPage.unlockTitle', { month: label })}
          </DialogTitle>
          <DialogDescription>
            {lock
              ? t('accountingPages.periodsPage.lockDescription', {
                  date: formatDate(action.period.endDate),
                })
              : t('accountingPages.periodsPage.unlockDescription')}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-3">
          <p className="text-sm">
            {t('accountingPages.periodsPage.months', { count: affected.length })}{' '}
            <span className="font-medium">{rangeLabel(affected)}</span>
          </p>
          <Field
            label={t('accountingPages.periodsPage.reason')}
            hint={t('accountingPages.periodsPage.reasonHint')}
          >
            {(id) => (
              <Textarea
                id={id}
                value={reason}
                maxLength={1000}
                autoFocus
                placeholder={
                  lock
                    ? t('accountingPages.periodsPage.lockPlaceholder')
                    : t('accountingPages.periodsPage.unlockPlaceholder')
                }
                onChange={(e) => setReason(e.target.value)}
              />
            )}
          </Field>
          {warnings ? (
            <div
              role="alert"
              className="rounded-md border border-warning/30 bg-warning/5 px-3 py-2 text-sm"
            >
              <p className="font-medium">{t('accountingPages.periodsPage.warningsTitle')}</p>
              <ul className="mt-1 list-disc ps-4">
                {warnings.map((w) => (
                  <li key={w}>{w}</li>
                ))}
              </ul>
            </div>
          ) : null}
          {error ? (
            <p role="alert" className="text-sm text-destructive">
              {m.error.message}
            </p>
          ) : null}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={close}>
            {t('common.cancel')}
          </Button>
          <Button
            variant={lock ? 'default' : 'destructive'}
            disabled={tooShort || m.isPending}
            onClick={() => m.mutate(Boolean(warnings))}
          >
            {m.isPending
              ? lock
                ? t('accountingPages.periodsPage.locking')
                : t('accountingPages.periodsPage.unlocking')
              : lock
                ? warnings
                  ? t('accountingPages.periodsPage.lockAnyway')
                  : t('accountingPages.periodsPage.lock')
                : t('accountingPages.periodsPage.unlock')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
